import React from "react";

import SaleService from "../../services/sale";
import UserService from "../../services/user";
import LoadingIndicator from "../../components/loading-indicator";

import Navigation from "../../components/navigation";
import Footer from "../../components/footer";
import ResponsiveNavigation from "../../components/responsive-navigation";
import PURCHASE_STATE from "../../constants/purchase-status";
import HelmetComponent from "../../components/helmet";

class Sales extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            sales: [],
            chatSales: [],
            subscriptionSales: [],
            publicNameByCognitoUsername: {},
            isLoading: false,
        };
    }

    componentDidMount() {
        this.fetchMySales();
        this.fetchMyChatSales();
        this.fetchMySubscriptionSales();
    }

    fetchMySales() {
        this.setState({ isLoading: true });
        SaleService.fetchMySales()
            .then(response => this.setState({ sales: this.sortSales(response.data.sales || []), isLoading: false }))
            .catch(() => this.setState({ sales: [], isLoading: false }));
    }

    fetchMyChatSales() {
        SaleService.fetchMyChatSales().then(response => {
            const chatSales = response.data.chat_sales || [];
            this.setState({ chatSales });
            this.hydratePublicNames(chatSales.map(s => s.buyer_sub));
        }).catch(() => {});
    }

    fetchMySubscriptionSales() {
        SaleService.fetchMySubscriptionSales().then(response => {
            const subscribers = response.data?.subscribers || [];
            console.log('[Sales] subscription sales:', subscribers.length, subscribers);
            this.setState({ subscriptionSales: subscribers });
        }).catch(err => {
            console.error('[Sales] failed to fetch subscription sales', err?.response?.status, err?.response?.data, err?.message);
        });
    }

    hydratePublicNames(cognitoUsernames) {
        const unique = [...new Set(cognitoUsernames.filter(Boolean))];
        unique.forEach(cognitoUsername => {
            if (this.state.publicNameByCognitoUsername[cognitoUsername] !== undefined) return;
            UserService.fetchByCognitoUsername(cognitoUsername).then(res => {
                const publicName = res?.data?.publicName || null;
                this.setState(prev => ({
                    publicNameByCognitoUsername: { ...prev.publicNameByCognitoUsername, [cognitoUsername]: publicName }
                }));
            }).catch(() => {});
        });
    }

    displayBuyer(cognitoUsername) {
        if (!cognitoUsername) return '—';
        const name = this.state.publicNameByCognitoUsername[cognitoUsername];
        return name ? `@${name}` : cognitoUsername;
    }

    sortSales(sales) {
        return sales.sort((a, b) => (b.date_created || 0) - (a.date_created || 0));
    }

    formatDate(date) {
        if (!date) return '—';
        return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    formatEpochDate(seconds) {
        return this.formatDate(seconds ? seconds * 1000 : null);
    }

    calculatePackagesRevenueBtc() {
        const total = this.state.sales.reduce((sum, sale) => (
            sale.status === PURCHASE_STATE.JOSE ? sum + parseFloat(sale.transaction?.jose || 0) : sum
        ), 0);
        return total.toFixed(6);
    }

    calculateChatRevenueUsd() {
        return this.state.chatSales.reduce((sum, s) => sum + Number(s.price || 0), 0).toFixed(2);
    }

    calculateSubscriptionMrrUsd() {
        return this.state.subscriptionSales
            .filter(s => s.status === 'active')
            .reduce((sum, s) => sum + Number(s.price || 0), 0)
            .toFixed(2);
    }

    activeSubscriberCount() {
        return this.state.subscriptionSales.filter(s => s.status === 'active').length;
    }

    initialFor(label) {
        const s = (label || '?').replace(/^@/, '');
        return (s[0] || '?').toUpperCase();
    }

    statusPillClass(status) {
        const s = (status || '').toLowerCase();
        if (s === 'active' || s === PURCHASE_STATE.JOSE) return 'sales-pill sales-pill--success';
        if (s === 'cancelled' || s === 'canceled') return 'sales-pill sales-pill--muted';
        if (s === 'pending') return 'sales-pill sales-pill--pending';
        return 'sales-pill';
    }

    renderSummaryCards() {
        const cards = [
            { label: 'Package sales', value: this.state.sales.length, sub: `${this.calculatePackagesRevenueBtc()} BTC` },
            { label: 'Active subscribers', value: this.activeSubscriberCount(), sub: `$${this.calculateSubscriptionMrrUsd()} / mo` },
            { label: 'Message sales', value: this.state.chatSales.length, sub: `$${this.calculateChatRevenueUsd()} lifetime` },
        ];
        return (
            <div className="sales-summary-grid">
                {cards.map((c, i) => (
                    <div key={i} className="sales-summary-card">
                        <div className="sales-summary-label">{c.label}</div>
                        <div className="sales-summary-value">{c.value}</div>
                        <div className="sales-summary-sub">{c.sub}</div>
                    </div>
                ))}
            </div>
        );
    }

    renderSectionHeader(title, count) {
        return (
            <div className="sales-section-header">
                <h2 className="sales-section-title">{title}</h2>
                <span className="sales-section-count">{count}</span>
            </div>
        );
    }

    renderEmptyState(message) {
        return (
            <div className="sales-empty">
                <div className="sales-empty-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                </div>
                <div className="sales-empty-text">{message}</div>
            </div>
        );
    }

    renderPartyCell(label) {
        return (
            <div className="sales-party">
                <div className="sales-party-avatar">{this.initialFor(label)}</div>
                <div className="sales-party-name">{label}</div>
            </div>
        );
    }

    renderPackageSales() {
        const { sales } = this.state;
        return (
            <section className="sales-section">
                {this.renderSectionHeader('Package Sales', sales.length)}
                {sales.length === 0
                    ? this.renderEmptyState("You haven't sold a package yet.")
                    : (
                        <div className="sales-table-wrap">
                            <table className="sales-table">
                                <thead>
                                    <tr>
                                        <th>Package</th>
                                        <th className="sales-th-right">Total</th>
                                        <th>Currency</th>
                                        <th>Status</th>
                                        <th className="sales-th-right">Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sales.map((sale, i) => {
                                        const isPaid = sale.status === PURCHASE_STATE.JOSE;
                                        return (
                                            <tr key={i}>
                                                <td>{sale.package?.name || '—'}</td>
                                                <td className="sales-td-right sales-td-mono">{isPaid ? sale.transaction?.jose : '—'}</td>
                                                <td>BTC</td>
                                                <td><span className={this.statusPillClass(sale.status)}>{isPaid ? 'Paid' : 'Pending'}</span></td>
                                                <td className="sales-td-right">{this.formatEpochDate(sale.date_created)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )
                }
            </section>
        );
    }

    renderSubscriptionSales() {
        const { subscriptionSales } = this.state;
        return (
            <section className="sales-section">
                {this.renderSectionHeader('Subscription Sales', subscriptionSales.length)}
                {subscriptionSales.length === 0
                    ? this.renderEmptyState('No subscription sales yet.')
                    : (
                        <div className="sales-table-wrap">
                            <table className="sales-table">
                                <thead>
                                    <tr>
                                        <th className="sales-th-party">Subscriber</th>
                                        <th className="sales-th-right">Price</th>
                                        <th>Status</th>
                                        <th>Started</th>
                                        <th>Renews / Ended</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subscriptionSales.map((sale, i) => {
                                        const label = sale.subscriberPublicName ? `@${sale.subscriberPublicName}` : (sale.subscriberSub || '—');
                                        return (
                                            <tr key={sale.subscriptionId || i}>
                                                <td>{this.renderPartyCell(label)}</td>
                                                <td className="sales-td-right sales-td-mono">${Number(sale.price || 0).toFixed(2)}</td>
                                                <td><span className={this.statusPillClass(sale.status)}>{sale.status || '—'}</span></td>
                                                <td>{this.formatEpochDate(sale.startedAt)}</td>
                                                <td>{this.formatEpochDate(sale.endsAt)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )
                }
            </section>
        );
    }

    renderChatSales() {
        const { chatSales } = this.state;
        return (
            <section className="sales-section">
                {this.renderSectionHeader('Message Sales', chatSales.length)}
                {chatSales.length === 0
                    ? this.renderEmptyState('No message sales yet.')
                    : (
                        <div className="sales-table-wrap">
                            <table className="sales-table">
                                <thead>
                                    <tr>
                                        <th className="sales-th-party">Buyer</th>
                                        <th className="sales-th-right">Amount</th>
                                        <th className="sales-th-right">Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {chatSales.map((sale, i) => (
                                        <tr key={i}>
                                            <td>{this.renderPartyCell(this.displayBuyer(sale.buyer_sub))}</td>
                                            <td className="sales-td-right sales-td-mono">${Number(sale.price || 0).toFixed(2)}</td>
                                            <td className="sales-td-right">{this.formatDate(sale.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                }
            </section>
        );
    }

    renderSalesContent() {
        return (
            <main className="sales-page">
                <div className="container">
                    <div className="sales-page-header">
                        <h1 className="sales-page-title">Your Sales</h1>
                        <p className="sales-page-subtitle">Revenue across packages, subscriptions, and paid messages.</p>
                    </div>
                    {this.renderSummaryCards()}
                    {this.renderPackageSales()}
                    {this.renderSubscriptionSales()}
                    {this.renderChatSales()}
                </div>
            </main>
        );
    }

    render() {
        return (
            <div>
                <header>
                    <div className="nav-container">
                        <div className="container">
                            <Navigation isAuthenticated={true} currentUser={this.props.currentUser} navigation={this.props.navigation} />
                        </div>
                    </div>
                </header>
                {this.state.isLoading ? <LoadingIndicator /> : this.renderSalesContent()}
                <Footer />
                <ResponsiveNavigation isAuthenticated={true} currentUser={this.props.currentUser} navigation={this.props.navigation} />
                <HelmetComponent title='Your Sales' description='OpenSwap.io sales page.' />
            </div>
        );
    }
}

export default Sales;
